import React, { useContext, useState, useCallback, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Typography,
  Box,
} from "@mui/material";
import AppContext from "../config/AppContext";
import { ReactComponent as ClearIconDark } from "../assets/images/ralvie/clearTextIconDark.svg";
import { ReactComponent as ClearIcon } from "../assets/images/ralvie/clearTextIconDark.svg";
import { ReactComponent as SearchIcon } from "../assets/images/ralvie/SearchIcon.svg";
import { useTranslation } from "react-i18next";
import { ReactComponent as ErrorIcon } from "../assets/images/ralvie/error-icon.svg";

const CustomProjectSelect = ({ options, label, value, onChange, error, disabled, helperText, placeholder, handleClear = () => { } }) => {
  const { mode } = useContext(AppContext);
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = React.useRef();
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const clearFilterName = () => {
    setSearchTerm("");
  };

  useEffect(() => {
  if (open && searchInputRef.current) {
    searchInputRef.current.focus();
  }
}, [filteredOptions, open]);

  useEffect(() => {
    const uniqueOptions = options.filter(
      (value, index, self) => index === self.findIndex((t) => t.id === value.id)
    );
    const filtered = uniqueOptions.filter((option) =>
      option.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  const handleChange = (event) => {
    const selectedValue =
      event.target.value === "No Project" ? "" : event.target.value;
    onChange(selectedValue);
    setSearchTerm("");
    setFilteredOptions(options);
    setOpen(false);
  };

  const handleClick = useCallback(() => {
    clearFilterName();
    setOpen((prevState) => !prevState);
  }, [open]);

  const handleMenuClose = () => {
    clearFilterName();
    setOpen(false);
  };

  const handleMenuOpen = () => {
    setOpen(true);
  };

  const styles = {
    textField: {
      "& .MuiOutlinedInput-root": {
        background: mode === "dark" ? "black" : "var(--background-color-9)",
        color: mode === "dark" ? "#f8f8f8" : "var(--text-color-6)",
        "& fieldset": {
          borderRadius: "none",
          border: "none",
        },
        "&.Mui-focused fieldset": {
          borderColor: "none",
        },
        "&:hover fieldset": {
          borderColor: "none",
        },
        "&.MuiInputLabel-root ": {
          display: "none",
          border: "none",
        },
        "&.Mui-focused": {
          backgroundColor: "var(-input-focused-background)",
        },
      },
    },
  };

  return (
    <FormControl fullWidth>
      <InputLabel shrink htmlFor="select-single">
        {label}
      </InputLabel>
      <Select
        fullWidth
        id="btn-select-options"
        value={value || ""}
        error={error}
        disabled={disabled}
        onChange={handleChange}
        disableRipple
        label={label}
        IconComponent={disabled ? () => null : undefined}
        renderValue={(selected) => {
          if (!selected || selected.length === 0) {
            return placeholder;
          }
          const selectedOption = options.find(
            (option) => option.id === selected
          );
          return selectedOption ? selectedOption.name : selected;
        }}
        input={
          <OutlinedInput
            label={""}
            endAdornment={
              !disabled && ( 
              <InputAdornment position="end" sx={{ display: 'flex', gap: 1 }}>
                {value && (
                  <IconButton onClick={handleClear}><ClearIcon height={"12px"} width={"12px"} /> </IconButton>
                )}
                <IconButton onClick={() => setOpen(!open)}>  </IconButton>
              </InputAdornment>
              )
            }
          />
        }
        inputProps={{
          id: "select-single",
        }}
        sx={{
          background: "var(--input-background)",
          borderRadius: "5px",
          color: "var(--text-primary)",
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 180,
              marginTop: "8px",
              overflowY: "auto",
              scrollbarWidth: "thin",
              background: mode === "dark" ? "#202020" : "",
              scrollbarColor: "var(--background-color-38) transparent",
              "&::-webkit-scrollbar": {
                width: "8px",
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: "10px",
                backgroundColor: "var(--background-color-38)",
              },
              "& .MuiMenuItem-root": {
                "&:hover": {
                  backgroundColor: mode === "dark" ? "#362C20" : "#FFF9F2",
                },
                "&.Mui-selected": {
                  backgroundColor: mode === "dark" ? "#362C20" : "#FFF9F2",
                  color: "#FA9C2B",
                },
                "&.Mui-selected:hover": {
                  backgroundColor: mode === "dark" ? "#362C20" : "#FFF9F2",
                  color: "#FA9C2B",
                },
              },
            },
          },
          MenuListProps: {
            sx: {
              paddingTop: "0px",
            },
          },
        }}
        displayEmpty
        open={open}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
      >
        {open && (
          <MenuItem
            id="dashboard-member-menuitem"
            onKeyDown={(e) => e.stopPropagation()}
            disableRipple
            sx={{
              fontFamily: "Gotham",
              fontStyle: "normal",
              fontVariant: "normal",
              fontWeight: "var(--fw-500)",
              backgroundColor:
                mode === "dark" ? "#1F1F1F !important" : "#FFFFFF !important",
              color: mode === "dark" ? "#f8f8f8cc" : "#242424cc",
              "&:hover": {
                backgroundColor: "#1F1F1F",
              },
              "&.Mui-selected, &.Mui-selected:hover": {
                backgroundColor:
                  mode === "dark" ? "#362C20!important" : "#FFF9F2!important",
                color: "#FA9C2B",
              },
              position: "sticky",
              top: "0px",
              zIndex: "1",
            }}
          >
            <TextField
              fullWidth
              inputRef={searchInputRef}
              id="text-search-member"
              name="text-search-member"
              placeholder={t("RAL0270")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              sx={{ ...styles.textField }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      id="project-clear-icon"
                      aria-label="clear input"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={() => {
                        clearFilterName();
                      }}
                      sx={{
                        borderRadius: "5px",
                        background: "transparent",
                        visibility: searchTerm ? "inherit" : "hidden",
                        "& .clear_svg": {
                          transition: "color 0.3s ease",
                          color:
                            mode === "dark"
                              ? "var(--color-33-50)"
                              : "#242424cc",
                        },
                        "&:hover": {
                          "& .hover-text, & .clear_svg": {
                            color: mode === "dark" ? "#f8f8f8cc" : "#242424",
                          },
                        },
                      }}
                      disableRipple
                    >
                      {searchTerm && (
                        <Tooltip title={t("RAL0693")}>
                          {mode === "dark" ? (
                            <ClearIconDark className="clear_svg" />
                          ) : (
                            <ClearIcon className="clear_svg" />
                          )}
                        </Tooltip>
                      )}
                    </IconButton>
                    <IconButton
                      id="project-search-icon"
                      sx={{
                        borderRadius: "5px",
                        background: "transparent",
                        "& .search_svg": {
                          transition: "color 0.3s ease",
                          color:
                            mode === "dark"
                              ? "var(--color-33-50)"
                              : "#242424cc",
                        },
                        "&:hover": {
                          "& .hover-text, & .search_svg": {
                            color: mode === "dark" ? "#f8f8f8cc" : "#242424",
                          },
                        },
                      }}
                      type="button"
                      aria-label="search"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                       onKeyDown={(e) => e.stopPropagation()}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      disableRipple
                    >
                      <Tooltip title={t("RAL0180")}>
                        <SearchIcon
                          id="project-search-icon"
                          className="search_svg"
                        />
                      </Tooltip>
                    </IconButton>
                  </InputAdornment>
                ),
                inputProps: {
                  maxLength: 50,
                },
              }}
            />
          </MenuItem>
        )}

        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>{t("RAL0284")}</MenuItem>
        )}
      </Select>
      {error && (
        <Box
          display={"flex"}
          alignItems={"center"}
          pt={0.5}
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
          }}
        >
          <Box pr={1} display="flex" alignItems="center">
            {<ErrorIcon />}
          </Box>
          <Box display="flex" alignItems="center">
            <Typography
              className="helper-text"
              color="error"
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              {helperText}
            </Typography>
          </Box>
        </Box>
      )}
    </FormControl>
  );
};

export default CustomProjectSelect;
